import { api } from './_init'
import { LOADING, UPDATE, FAIL, EMPTY } from '../reducers/default'
import { maxBy, minBy, orderBy } from 'lodash'
import { v4 } from 'uuid'

export const fetchSalesPlot = async (dispatch, event_id, translation, showPercentage) => {
  dispatch({ type: LOADING })
  if (event_id) {
    try {
      const result = await api.get(`/events/${event_id}/lookout_prediction?type=${showPercentage ? 'capacity' : 'ticket_count'}`)

      const series = {
        ticketsSold: [],
        fdPrediction: [],
      }

      const ticketsSold = []

      result.data.sales.forEach((point) => {
        // const x = point.percentile_bin
        const date = new Date(point.mapped_date)
        const x = Date.UTC(
          date.getUTCFullYear(),
          date.getUTCMonth(),
          date.getUTCDate(),
          date.getUTCHours(),
          date.getUTCMinutes(),
          date.getUTCSeconds(),
        )

        if (point.tickets_sold_actual >= 0) {
          const y = showPercentage
            ? Number((point.tickets_sold_actual * 100).toFixed(3))
            : point.tickets_sold_actual
          ticketsSold.push([x, y])
        }

        if (point.tickets_sold_predicted >= 0) {
          const y = showPercentage
            ? Number((point.tickets_sold_predicted * 100).toFixed(3))
            : point.tickets_sold_predicted
          series.fdPrediction.push([x, y])
        }
      })

      const reversedTicketsSold = ticketsSold.reverse()
      const firstValidTicketIndex = reversedTicketsSold.findIndex((t) => t[1] > 0)
      if (firstValidTicketIndex > 0) {
        reversedTicketsSold.splice(0, firstValidTicketIndex)
      }

      series.ticketsSold = reversedTicketsSold.reverse()

      // Tickets sold shouldn't appear if no sales were made.
      if (series.ticketsSold.every((t) => t[1] === 0)) {
        series.ticketsSold = []
      }

      if (series.fdPrediction.length > 0 || series.ticketsSold.length > 0) {
        // data: Expecting [X, Y] where y is the ticket count
        const chartData = [
          {
            name: translation.fdPrediction,
            color: '#F58562',
            data: series.fdPrediction,
          },
          {
            name: translation.ticketsSold,
            color: '#1F5274',
            data: series.ticketsSold,
          },
        ]
        dispatch({ type: UPDATE, payload: chartData })
      } else {
        dispatch({ type: EMPTY, payload: 'Event.salesTrend.emptySalesTrends' })
      }
    } catch (error) {
      dispatch({ type: FAIL })
    }
  }
}

// Blue tones, darkest first, so the largest share (sorted first) reads as most prominent.
const PRICE_CATEGORY_COLORS = ['#1F5274', '#2b6b94', '#47728e', '#5A84A0', '#5FA2CE', '#94BCD7']

const replaceLabel = (number) => {
  let numeral = 'I'

  if (number === 2) {
    numeral = 'II'
  }
  if (number === 3) {
    numeral = 'III'
  }
  if (number === 4) {
    numeral = 'IV'
  }
  if (number === 5) {
    numeral = 'V'
  }
  if (number === 6) {
    numeral = 'VI'
  }
  if (number === 7) {
    numeral = 'VII'
  }
  if (number === 8) {
    numeral = 'VIII'
  }
  if (number === 9) {
    numeral = 'IX'
  }
  if (number === 10) {
    numeral = 'X'
  }
  if (number === 11) {
    numeral = 'XI'
  }
  if (number === 12) {
    numeral = 'XII'
  }
  if (number === 13) {
    numeral = 'XIII'
  }
  if (number === 14) {
    numeral = 'XIV'
  }
  if (number === 15) {
    numeral = 'XV'
  }

  return `Kategorie ${numeral}`
}

/**
 * Label of a single price category of `price_categories`.
 */
const priceCategoryName = (key) => {
  const name = key.replace(/_/g, ' ')
  if (!isNaN(Number(name))) return replaceLabel(Number(name))
  return name.charAt(0).toUpperCase() + name.slice(1)
}

// Ticket type that is always rendered last, no matter its share.
export const FREE_TICKET_TYPE = 'free'

const TICKET_TYPE_FALLBACK_COLORS = ['#5FA2CE', '#94BCD7', '#2b6b94', '#47728e', '#162d3c']

/**
 * Label and color of a single ticket type of `tickets_per_category`.
 */
const ticketTypeMeta = (key, translation, fallbackIndex = 0) => {
  switch (key) {
    case 'regular':
      return { name: translation.fullPrice, colorClass: 'bg-navy', color: '#1F5274' }
    case 'regular_discount':
      return { name: translation.regularDiscount, colorClass: 'bg-orange', color: '#F58562' }
    case 'discount':
      return { name: translation.discount, colorClass: 'bg-amber', color: '#f0b429' }
    case 'subscription':
      return { name: translation.subscription, colorClass: 'bg-green', color: '#4aa480' }
    case 'reserved':
      return { name: translation.reserved, colorClass: 'bg-navy-75', color: '#6790a6' }
    case FREE_TICKET_TYPE:
      return { name: translation.free, colorClass: 'bg-navy-50', color: '#a8bfcb' }
    default: {
      let name = key.replace('share_', '').replace('_tickets', '').replace(/_/g, ' ')
      name = name.charAt(0).toUpperCase() + name.slice(1) // Capitalization
      return {
        name,
        colorClass: 'bg-navy-75',
        color: TICKET_TYPE_FALLBACK_COLORS[fallbackIndex % TICKET_TYPE_FALLBACK_COLORS.length],
      }
    }
  }
}

export const UNSOLD_KEY = 'unsold'
const UNSOLD_COLOR = '#eaeff2'

const CANCELLED_TICKET_TYPE = 'cancelled'

/**
 * Absolute number of tickets sold, all time: per ticket type and in total.
 *
 * `/events/[eid]/tickets` only returns rounded shares, so the counts come from
 * the daily transaction summary — the same DailyRevenueSummary data those shares
 * are calculated from. Ticket counts are currency independent, but the summary
 * is queried per currency, so every currency of the event is summed up.
 */
const fetchTicketSales = async (event_id) => {
  const currenciesResult = await api.get('/daily_revenue_summary/available_currencies', {
    params: { event_id },
  })

  const currencies = currenciesResult.data?.currencies?.length
    ? currenciesResult.data.currencies
    : ['<empty>']

  const summaryResults = await Promise.all(
    currencies.map((currency) =>
      api.get('/daily_revenue_summary/', {
        params: { currency, start_date: '1900-01-01', end_date: '2100-01-01', event_id },
      }),
    ),
  )

  const counts = {}
  let soldTotal = 0
  summaryResults.forEach((result) => {
    // `manual_adjustments` is only a highlight of entries already contained in
    // `summaries`, adding it up as well would count those sales twice.
    const summaries = result.data?.summaries ?? []
    summaries.forEach((day) => {
      Object.entries(day.number_of_tickets?.parts ?? {}).forEach(([ticketType, count]) => {
        // `cancelled` is not a ticket type of its own but a negative correction
        // on the tickets of that day, and part of its `total`. Leaving it out of
        // the per type counts but keeping it in the total takes the cancelled
        // tickets off the whole bar rather than off a single type.
        soldTotal += count
        if (ticketType === CANCELLED_TICKET_TYPE) return
        counts[ticketType] = (counts[ticketType] ?? 0) + count
      })
    })
  })

  return { counts, soldTotal }
}

/**
 * The share of the capacity that has been taken by tickets, cancellations
 * already deducted. An oversold event still fills the bar exactly once.
 */
const soldCapacityShare = (soldTotal, capacity) => Math.min(soldTotal / capacity, 1)

// Remainders below this are rounding noise rather than unsold capacity.
const MIN_UNSOLD_PERCENTAGE = 0.01

/**
 * Closes the bar with the part of the capacity that hasn't been sold yet, so
 * both breakdowns end on the same unsold share.
 */
const appendUnsoldRemainder = (chartData, translation) => {
  const unsoldPercentage = 100 - chartData.reduce((sum, segment) => sum + segment.y, 0)
  if (unsoldPercentage < MIN_UNSOLD_PERCENTAGE) return

  chartData.push({
    key: UNSOLD_KEY,
    name: translation.unsold,
    color: UNSOLD_COLOR,
    y: unsoldPercentage,
    empty: true,
  })
}

/**
 * Spreads segments that are shares of the tickets sold over the sold part of the
 * capacity, so together they fill exactly that much of the bar. The shares can
 * add up to slightly more or less than the whole (rounding, or the cancelled
 * tickets that belong to no type), which is why they are normalized first.
 */
const spreadOverSoldShare = (chartData, soldShare) => {
  const shareTotal = chartData.reduce((sum, segment) => sum + segment.y, 0)
  chartData.forEach((segment) => {
    segment.y = (segment.y / shareTotal) * soldShare * 100
  })
}

/**
 * The event capacity as one 100% wide bar, filled up by ticket type: every
 * segment is that ticket type's share of the total capacity, the remainder is
 * the part that hasn't been sold yet. Sold types are sorted from the biggest to
 * the smallest share with free tickets last, the unsold remainder always last.
 */
export const fetchTicketTypesCapacitySplit = async (dispatch, event_id, translation, capacity) => {
  dispatch({ type: LOADING })
  if (event_id) {
    // Without a capacity there is nothing to fill up, so there is nothing to show.
    if (!(capacity > 0)) {
      dispatch({ type: EMPTY, payload: 'Event.salesTrend.emptySalesTrends' })
      return
    }

    try {
      const { counts, soldTotal } = await fetchTicketSales(event_id)
      const soldTypes = Object.keys(counts).filter((key) => counts[key] > 0)
      const soldShare = soldCapacityShare(soldTotal, capacity)

      if (!soldTypes.length || !(soldShare > 0)) {
        dispatch({ type: EMPTY, payload: 'Event.salesTrend.emptySalesTrends' })
        return
      }

      const chartData = soldTypes.map((key, index) => {
        const { name, color } = ticketTypeMeta(key, translation, index)
        return { key, name, color, y: counts[key] }
      })

      spreadOverSoldShare(chartData, soldShare)

      chartData.sort((a, b) => {
        const aIsFree = a.key === FREE_TICKET_TYPE
        const bIsFree = b.key === FREE_TICKET_TYPE
        if (aIsFree !== bIsFree) return aIsFree ? 1 : -1
        return b.y - a.y
      })

      appendUnsoldRemainder(chartData, translation)

      dispatch({ type: UPDATE, payload: chartData })
    } catch (error) {
      dispatch({ type: FAIL })
    }
  }
}

/**
 * The event capacity as one 100% wide bar, filled up by price category: the
 * shares of `/events/[eid]/tickets` are shares of the tickets sold, so they get
 * scaled down to the share of the capacity that has been sold — the same sold
 * share the ticket type breakdown ends on — and the rest of the bar stays empty.
 * Categories are sorted from the biggest to the smallest share.
 */
export const fetchTicketCategoriesCapacitySplit = async (
  dispatch,
  event_id,
  translation,
  capacity,
) => {
  dispatch({ type: LOADING })
  if (event_id) {
    // Without a capacity there is nothing to fill up, so there is nothing to show.
    if (!(capacity > 0)) {
      dispatch({ type: EMPTY, payload: 'Event.salesTrend.emptySalesTrends' })
      return
    }

    try {
      const [ticketsResult, { soldTotal }] = await Promise.all([
        api.get(`/events/${event_id}/tickets`),
        fetchTicketSales(event_id),
      ])

      const priceCategories = ticketsResult.data.price_categories ?? {}
      const soldShare = soldCapacityShare(soldTotal, capacity)

      const chartData = Object.keys(priceCategories)
        .filter((key) => key !== 'eid' && priceCategories[key] > 0)
        .map((key) => ({ key, name: priceCategoryName(key), y: priceCategories[key] }))

      if (!chartData.length || !(soldShare > 0)) {
        dispatch({ type: EMPTY, payload: 'Event.salesTrend.emptySalesTrends' })
        return
      }

      spreadOverSoldShare(chartData, soldShare)

      chartData.sort((a, b) => b.y - a.y)
      chartData.forEach((segment, index) => {
        segment.color = PRICE_CATEGORY_COLORS[index % PRICE_CATEGORY_COLORS.length]
      })

      appendUnsoldRemainder(chartData, translation)

      dispatch({ type: UPDATE, payload: chartData })
    } catch (error) {
      dispatch({ type: FAIL })
    }
  }
}

export const fetchTopClusters = async (dispatch, event_id) => {
  dispatch({ type: LOADING })
  if (event_id) {
    try {
      const result = await api.get(`/events/${event_id}/clusters`)

      const rawData = result.data.clusters

      const chartData = []
      rawData.forEach((point, index) => {
        /* value is 0 - 5 and we are transfering it to % */
        const value = point.metric_pred
        const valueInPer = (value / 5) * 100
        chartData.push({ key: `c-${index}`, name: point.tc, y: valueInPer })
      })

      chartData.sort((a, b) => (a.y < b.y ? 1 : -1))

      if (chartData.length > 0) {
        dispatch({ type: UPDATE, payload: chartData })
      } else if (result && result.error) {
        dispatch({ type: FAIL })
      } else {
        dispatch({ type: EMPTY, payload: 'Event.salesTrend.emptySalesTrends' })
      }
    } catch (error) {
      dispatch({ type: FAIL })
    }
  }
}

const mapToSeries = (data) => {
  let series = { revenue: [], tickets: [] }
  for (let i = 0; i < data.length; i++) {
    const el = data[i]
    const date = new Date(el.date)
    const x = Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate(),
      date.getUTCHours(),
      date.getUTCMinutes(),
      date.getUTCSeconds(),
    )

    series.revenue = [...series.revenue, { x: x, y: el.revenue?.total, parts: el.revenue?.parts, id: v4() }]
    series.tickets = [
      ...series.tickets,
      { x: x, y: el.number_of_tickets?.total, parts: el.number_of_tickets?.parts, id: v4() },
    ]
  }
  return series
}

export const fetchDailyRevenue = async (dispatch, event_id, userCurrency) => {
  dispatch({ type: LOADING })

  if (event_id) {
    try {
      const result = await api.get('/daily_revenue_summary/', {
        params: {
          currency: userCurrency,
          start_date: '1900-01-01',
          end_date: '2100-01-01',
          event_id,
        },
      })

      if (result.status === 200) {
        let dailySummary = mapToSeries(result?.data?.summaries ?? [])
        let manualAdjustments = { revenue: [], tickets: [] }

        if (result.data.manual_adjustments?.length > 0) {
          const paddingDays = 2 * 24 * 60 * 60 * 1000 // 2 days
          manualAdjustments = mapToSeries(result?.data?.manual_adjustments ?? [])
          addManualAdjustmentsEdgePadding(manualAdjustments, dailySummary, paddingDays)

          manualAdjustments = {
            revenue: orderBy(manualAdjustments.revenue, ['x']),
            tickets: orderBy(manualAdjustments.tickets, ['x']),
          }
        }

        dailySummary = {
          revenue: orderBy(dailySummary.revenue, ['x']),
          tickets: orderBy(dailySummary.tickets, ['x']),
        }

        const hasData =
          (dailySummary.tickets.length > 0 && dailySummary.revenue.length > 0) ||
          (manualAdjustments.tickets.length > 0 && manualAdjustments.revenue.length > 0)
        dispatch({
          type: UPDATE,
          payload: {
            dailySummary,
            manualAdjustments,
            hasData,
          },
        })
      }
    } catch (error) {
      console.log(error)
    }
  }
}

/**
 * Adds padding to the edge of the daily summary data based on the manual adjustments data.
 * This is likely not needed anymore, since we're not adjusting plot-lines x position anymore.
 *
 * @param {Object} manualAdjustments - The manual adjustments data.
 * @param {Object} dailySummary - The daily summary data.
 * @param {number} paddingDays - The number of padding days to add.
 */
/**
 * Adds manual adjustments edge padding to the daily summary.
 *
 * @param {Object} manualAdjustments - The manual adjustments object.
 * @param {Object} dailySummary - The daily summary object.
 * @param {number} paddingDays - The number of padding days to add.
 */
function addManualAdjustmentsEdgePadding(manualAdjustments, dailySummary, paddingDays) {
  if (!manualAdjustments) {
    return
  }

  const minAdjustmentDate = Math.min(minBy(manualAdjustments.tickets, 'x').x, minBy(manualAdjustments.revenue, 'x').x)
  const minDailySummaryDate = Math.min(
    minBy(dailySummary.tickets, 'x')?.x ?? Infinity,
    minBy(dailySummary.revenue, 'x').x ?? Infinity,
  )

  if (minAdjustmentDate < minDailySummaryDate) {
    // add a fake entry to daily summary
    const padding = {
      x: minAdjustmentDate - paddingDays,
      y: null,
      parts: {},
      id: v4(),
      fake: true,
    }
    dailySummary.tickets.push(padding)
    dailySummary.revenue.push(padding)
  }

  const maxAdjustment = Math.max(maxBy(manualAdjustments.tickets, 'x').x, maxBy(manualAdjustments.revenue, 'x').x)
  const maxDailySummaryDate = Math.max(
    maxBy(dailySummary.tickets, 'x')?.x ?? Infinity,
    maxBy(dailySummary.revenue, 'x')?.x ?? Infinity,
  )
  if (maxAdjustment >= maxDailySummaryDate) {
    // add a fake entry to daily summary
    const padding = {
      x: maxAdjustment + paddingDays,
      y: null,
      parts: {},
      id: v4(),
      fake: true,
    }
    dailySummary.tickets.push(padding)
    dailySummary.revenue.push(padding)
  }
}
